import { useRef, useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import I18n from "i18n-js";
import { RootState } from "app/store";
import { WarningMessageBox } from "common/elements";
import { btnDelay, usePlaySound } from "common/utils";
import { AppUser, AccountType, BillingType } from "features/user/types";
import { RouteProps } from "../type";
import { GoBackBtn } from "../GoBackBtn";
import { UserStatus } from "./UserStatus";
import { UserSetting } from "./UserSetting";
import { UserDisplayName } from "./UserDisplayName";
import { CreateParentsKidsUser } from "./CreateParentsKidsUser";

export const UserList = (props: RouteProps) => {
  const { navigate, close } = props;
  const play = usePlaySound();
  const ref = useRef<HTMLDivElement>(null);
  const handleGoBack = () => navigate("/");
  const [maxWarning, setMaxWarning] = useState(false);
  const users = useSelector((state: RootState) => state.user.users);
  const parent = useSelector((state: RootState) => state.user.parent);
  const active = useSelector((state: RootState) => state.user.active);
  const [selectedUser, setSelectedUser] = useState<AppUser>(active);
  const sortedUsers = useMemo(
    () =>
      users
        .slice()
        .filter((u) => u.account_type !== AccountType.PARENTS)
        .sort((a, b) => (dayjs(a.created_at) < dayjs(b.created_at) ? -1 : 1)),
    [users],
  );

  useEffect(() => {
    if (active) {
      setSelectedUser(
        users.filter((u) => u.uid === selectedUser?.uid).shift() ?? active,
      );
    } else {
      setSelectedUser(null);
      close();
    }
  }, [users]);

  const handleCloseWarning = () => {
    play();
    btnDelay(() => {
      setMaxWarning(false);
    });
  };

  return (
    <div className="flex-col-center w-full h-full p-4 pb-0">
      {maxWarning && (
        <WarningMessageBox
          zIndex={9999999}
          title={I18n.t(
            "MSG_SETTING_PARENTS_ADD_DEVICE_USER_MAX_WARNING_TITLE",
          )}
          message={I18n.t(
            "MSG_SETTING_PARENTS_ADD_DEVICE_USER_MAX_WARNING_MESSAGE",
          )}
          close={handleCloseWarning}
        />
      )}

      <div className="flex-row-el w-full flex-grow overflow-hidden gap-2">
        <div className="flex-col-el justify-start">
          <div className="flex-col-el items-center justify-start min-w-[362px] invisible-scrollbar">
            {sortedUsers.map((user) => (
              <div
                key={user.uid}
                onClick={() => {
                  play();
                  setSelectedUser(user);
                  if (ref.current) {
                    ref.current.scrollTop = 0;
                  }
                }}
                className={`flex-row-el items-center justify-start relative p-4 rounded-2xl cursor-pointer ${
                  selectedUser?.uid === user.uid
                    ? "bg-green-60"
                    : "bg-transparent"
                }`}
              >
                <div
                  className="flex-col-center bg-white rounded-full w-[80px] h-[80px]"
                  style={{
                    boxShadow:
                      "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                  }}
                >
                  <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden">
                    <img
                      key={user.icon}
                      alt="ユーザプロフィール画像"
                      src={user.icon}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>

                <div className="flex-col-el w-[250px] px-4">
                  <p className="text-body text-left text-textcolor-black text-font-size-body2-narrow w-full text-body-ellipsis">
                    {user.display_name}
                  </p>
                  <p className="text-body text-left text-gray-80 text-font-caption2-narrow">
                    {I18n.t("MSG_SETTING_PARENTS_USER_KIDS_PROFILE_CREATED_AT")}
                    :{dayjs(user.created_at).format("YYYY-MM-DD")}
                  </p>
                  <p className="text-body text-left text-gray-80 text-font-caption2-narrow">
                    {I18n.t("MSG_SETTING_PARENTS_USER_PROFILE_LATEST_AT")}:
                    {dayjs(user.last_sign_in_time).format("YYYY-MM-DD")}
                  </p>
                </div>
              </div>
            ))}

            {parent.billing_type !== BillingType.EDUCATION && (
              <CreateParentsKidsUser />
            )}
          </div>
        </div>

        {selectedUser && (
          <div className="flex-col-center flex-1 gap-5 bg-white/60 rounded-2xl">
            <div
              ref={ref}
              className="flex-col-el visible-scrollbar w-full py-14"
            >
              <UserDisplayName selectedUser={selectedUser} />

              <UserStatus selectedUser={selectedUser} />

              <UserSetting selectedUser={selectedUser} />
            </div>
          </div>
        )}
      </div>

      <div className="flex-row-center my-2">
        <GoBackBtn handleGoBack={handleGoBack} />
      </div>
    </div>
  );
};
